import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FaCar, FaFileAlt, FaSearch, FaPrint, FaEdit, FaTrash,
  FaCalendarAlt, FaUser, FaIdCard, FaTimesCircle,
  FaMoneyBillWave, FaPlus, FaFileExport, FaFilter,
  FaCalendarDay, FaCalendarWeek, FaSync,
  FaCreditCard, FaMoneyBill, FaBarcode, FaQrcode, FaWallet,
  FaChevronLeft, FaChevronRight, FaStepBackward, FaStepForward, FaStickyNote
} from 'react-icons/fa';
import Header from '../others/Header';
import config from '../config/config';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import { useToast } from '../components/ToastContext';
import ConfirmModal from '../components/ConfirmModal';

Modal.setAppElement('#root');

function OrdemServicoView() {
  const { API_BASE_URL } = config;
  const navigate = useNavigate();

  const [ordens, setOrdens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ordensFiltradas, setOrdensFiltradas] = useState([]);
  const [modalImpressaoIsOpen, setModalImpressaoIsOpen] = useState(false);
  const [ordemSelecionada, setOrdemSelecionada] = useState(null);
  const [tipoImpressao, setTipoImpressao] = useState('EMPRESA_CLIENTE');
  const [valorConfirm, setValorConfirm] = useState('Excluir');

  // Novos estados para a modal de finalização
  const [modalFinalizacaoIsOpen, setModalFinalizacaoIsOpen] = useState(false);
  const [ordemParaFinalizar, setOrdemParaFinalizar] = useState(null);
  const [formaPagamento, setFormaPagamento] = useState('');
  const [valorRecebido, setValorRecebido] = useState('');

  // Observação da OS aberta pelo ícone do card: { ordem, texto } (null = fechada)
  const [observacaoAberta, setObservacaoAberta] = useState(null);
  const [salvandoObservacao, setSalvandoObservacao] = useState(false);
  const MAX_OBSERVACAO = 255; // tamanho da coluna no banco

  // PUT /ordemservico/{id}/observacoes — vale em qualquer status (não mexe em valores); em branco limpa
  const salvarObservacao = async () => {
    const { ordem, texto } = observacaoAberta;
    setSalvandoObservacao(true);
    try {
      const { data } = await axios.put(`${API_BASE_URL}/ordemservico/${ordem.id}/observacoes`, { observacoes: texto }, getAuthHeader());
      setOrdens((lista) => lista.map((o) => (o.id === ordem.id ? { ...o, observacoes: data.observacoes } : o)));
      setObservacaoAberta(null);
      addToast('success', data.observacoes ? 'Observação salva!' : 'Observação removida.');
    } catch (err) {
      addToast('error', err.response?.data?.message || 'Não foi possível salvar a observação');
    } finally {
      setSalvandoObservacao(false);
    }
  };

  const formasPagamento = [
    { id: 'DINHEIRO', nome: 'Dinheiro', icone: FaMoneyBill, cor: '#27ae60' },
    { id: 'PIX', nome: 'PIX', icone: FaQrcode, cor: '#32cd32' },
    { id: 'DEBITO', nome: 'Cartão Débito', icone: FaCreditCard, cor: '#3498db' },
    { id: 'CREDITO', nome: 'Cartão Crédito', icone: FaCreditCard, cor: '#9b59b6' },
    { id: 'BOLETO', nome: 'Boleto Bancário', icone: FaBarcode, cor: '#e74c3c' }
  ];

  // Estados para filtros na tela principal
  const [filtroCpfCnpj, setFiltroCpfCnpj] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [modoFiltro, setModoFiltro] = useState('hoje');
  const [filtroStatus, setFiltroStatus] = useState('TODOS');
  const { addToast } = useToast();

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    message: '',
    onConfirm: () => { },
    title: 'Confirmação'
  });
  const [paginaAtual, setPaginaAtual] = useState(0);
  const [tamanhoPagina, setTamanhoPagina] = useState(50);
  const [totalPaginas, setTotalPaginas] = useState(0);
  const [totalElementos, setTotalElementos] = useState(0);

  // Função para mostrar confirmação
  const showConfirm = (message, onConfirm, title = 'Confirmação') => {
    setConfirmModal({
      isOpen: true,
      message,
      onConfirm,
      title
    });
  };

  // Função para fechar o modal
  const closeConfirm = () => {
    setConfirmModal({
      isOpen: false,
      message: '',
      onConfirm: () => { },
      title: 'Confirmação'
    });
  };

  // Obter token do localStorage
  const getAuthHeader = () => {
    const token = localStorage.getItem('jwtToken');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  // Função para formatar data para YYYY-MM-DD
  const formatarDataParaAPI = (data) => {
    return data.toISOString().split('T')[0];
  };

  // Carregar ordens com base no modo de filtro selecionado
  const carregarOrdens = async (pagina = paginaAtual) => {
    try {
      setLoading(true);
      let url = `${API_BASE_URL}/ordemservico/pagina?page=${pagina}&size=${tamanhoPagina}`;
      if (modoFiltro === 'hoje') {
        url = `${API_BASE_URL}/ordemservico/hoje/pagina?page=${pagina}&size=${tamanhoPagina}`;
      } else if (modoFiltro === 'periodo' && dataInicio && dataFim) {
        url = `${API_BASE_URL}/ordemservico/data/${dataInicio}/${dataFim}/pagina?page=${pagina}&size=${tamanhoPagina}`;
      } else if (modoFiltro === 'cpfCnpj' && filtroCpfCnpj) {
        url = `${API_BASE_URL}/ordemservico/cpfCnpj/${filtroCpfCnpj}/pagina?page=${pagina}&size=${tamanhoPagina}`;
      }

      const response = await axios.get(url, getAuthHeader());
      const dadosPagina = response.data;

      setOrdens(dadosPagina.content);
      setOrdensFiltradas(dadosPagina.content);
      setTotalPaginas(dadosPagina.totalPages);
      setTotalElementos(dadosPagina.totalElements);
      setPaginaAtual(dadosPagina.number);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao carregar ordens de serviço');
    } finally {
      setLoading(false);
    }
  };

  // Funções de paginação
  const irParaPagina = (pagina) => {
    setPaginaAtual(pagina);
    carregarOrdens(pagina);
  };

  const avancarPagina = () => {
    if (paginaAtual < totalPaginas - 1) {
      irParaPagina(paginaAtual + 1);
    }
  };

  const retrocederPagina = () => {
    if (paginaAtual > 0) {
      irParaPagina(paginaAtual - 1);
    }
  };

  const irParaPrimeiraPagina = () => {
    irParaPagina(0);
  };

  const irParaUltimaPagina = () => {
    irParaPagina(totalPaginas - 1);
  };

  const alterarTamanhoPagina = (novoTamanho) => {
    setTamanhoPagina(novoTamanho);
    setPaginaAtual(0);
  };
  // Carregar ordens ao inicializar e quando o modo de filtro mudar
  useEffect(() => {
    carregarOrdens(0);
  }, [modoFiltro, tamanhoPagina]);


  // Aplicar filtro de status
  useEffect(() => {
    if (filtroStatus === 'TODOS') {
      setOrdensFiltradas(ordens);
    } else {
      const filtradas = ordens.filter(ordem => ordem.statusOrdemServico === filtroStatus);
      setOrdensFiltradas(filtradas);
    }
  }, [filtroStatus, ordens]);

  // Validar período (máximo 90 dias)
  const validarPeriodo = (inicio, fim) => {
    const dataInicio = new Date(inicio);
    const dataFim = new Date(fim);
    const diffTime = Math.abs(dataFim - dataInicio);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 90;
  };

  // Aplicar filtro de período
  const aplicarFiltroPeriodo = () => {
    if (!dataInicio || !dataFim) {
      addToast('warning', 'Por favor, preencha ambas as datas');
      return;
    }

    if (!validarPeriodo(dataInicio, dataFim)) {
      addToast('warning', 'O período não pode ser superior a 90 dias.');
      return;
    }

    setModoFiltro('periodo');
    carregarOrdens();
  };

  // Aplicar filtro por CPF/CNPJ
  const aplicarFiltroCpfCnpj = () => {
    if (!filtroCpfCnpj) {
      addToast('warning', 'Por favor, informe um CPF ou CNPJ');
      return;
    }

    setModoFiltro('cpfCnpj');
    carregarOrdens();
  };

  // Limpar todos os filtros e carregar ordens do dia
  const limparFiltros = () => {
    setFiltroCpfCnpj('');
    setDataInicio('');
    setDataFim('');
    setFiltroStatus('TODOS');
    setModoFiltro('hoje');
    carregarOrdens();
  };

  // Função para formatar data
  const formatarData = (dataString) => {
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return new Date(dataString).toLocaleDateString('pt-BR', options);
  };

  // Função para editar ordem
  const editarOrdem = (id) => {
    navigate(`/criar-editar-ordemServico/${id}`);
  };

  // Função para criar nova ordem
  const novaOrdem = () => {
    navigate('/criar-editar-ordemServico');
  };

  // Função para excluir ordem
  const excluirOrdem = async (id) => {
    setValorConfirm('Excluir Ordem')
    showConfirm(
      'Tem certeza que deseja excluir esta ordem de serviço?',
      async () => {
        try {
          await axios.delete(`${API_BASE_URL}/ordemservico/${id}`, getAuthHeader());
          carregarOrdens();
        } catch (err) {
          addToast('error', 'Erro ao excluir ordem: ' + (err.response?.data?.message || err.message));
        }
      }, 'Excluir Ordem de serviço'
    );
  };

  const abrirModalImpressao = (ordem) => {
    setOrdemSelecionada(ordem);
    setModalImpressaoIsOpen(true);
  };

  // Função para fechar modal de impressão
  const fecharModalImpressao = () => {
    setModalImpressaoIsOpen(false);
    setOrdemSelecionada(null);
    setTipoImpressao('EMPRESA_CLIENTE');
  };

  // Função para imprimir ordem
  const imprimirOrdem = async (id, tipo = 'EMPRESA_CLIENTE') => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/ordemservico/${id}/imprimir?tipo=${tipo}`,
        {
          ...getAuthHeader(),
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const newWindow = window.open(url, '_blank');

      if (!newWindow) {
        const link = document.createElement('a');
        link.href = url;
        link.download = `ordem-servico-${id}-${tipo.toLowerCase()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      fecharModalImpressao();

    } catch (error) {
      addToast('error', 'Erro ao abrir PDF: ' + error.message);
      fecharModalImpressao();
    }
  };

  // Função para confirmar impressão
  const confirmarImpressao = () => {
    if (ordemSelecionada) {
      imprimirOrdem(ordemSelecionada.id, tipoImpressao);
    }
  };

  // Função para alterar status
  const alterarStatus = async (id, novoStatus) => {
    if (novoStatus === 'CANCELADA') {
      setValorConfirm('Cancelar Ordem')
      showConfirm(
        'Tem certeza que deseja cancelar esta ordem de serviço? Ao cancelar, não será possível editá-la.',
        () => {
          alterarSatusOrdem(id, novoStatus)
        }, 'Cancelar Ordem de serviço'
      );
    } else {
      alterarSatusOrdem(id, novoStatus)
    }
  };

  const alterarSatusOrdem = async (id, novoStatus) => {
    try {
      await axios.put(
        `${API_BASE_URL}/ordemservico/${id}/status`,
        { status: novoStatus },
        getAuthHeader()
      );
      carregarOrdens();
    } catch (err) {
      addToast('error', 'Erro ao alterar status: ' + (err.response?.data?.message || err.message));
    }
  }

  // Cores para cada status
  const getStatusColor = (status) => {
    switch (status) {
      case 'ABERTA':
        return { bg: '#E3F2FD', text: '#0D47A1' };
      case 'CONCLUIDA':
        return { bg: '#E8F5E9', text: '#2E7D32' };
      case 'EM_ANALISE':
        return { bg: '#FFF8E1', text: '#FF8F00' };
      case 'CANCELADA':
        return { bg: '#FFEBEE', text: '#C62828' };
      case 'PENDENTE':
        return { bg: '#F3E5F5', text: '#6A1B9A' };
      default:
        return { bg: '#ECEFF1', text: '#37474F' };
    }
  };

  // Funções para modal de finalização
  const abrirModalFinalizacao = (ordem) => {
    setOrdemParaFinalizar(ordem);
    setFormaPagamento('');
    setValorRecebido('');
    setModalFinalizacaoIsOpen(true);
  };

  const fecharModalFinalizacao = () => {
    setModalFinalizacaoIsOpen(false);
    setOrdemParaFinalizar(null);
    setFormaPagamento('');
    setValorRecebido('');
  };

  const calcularTroco = () => {
    if (!ordemParaFinalizar || !valorRecebido) return 0;

    const valorTotal = parseFloat(ordemParaFinalizar.valorTotal);
    const valorPago = parseFloat(valorRecebido);

    if (valorPago >= valorTotal) {
      return valorPago - valorTotal;
    }
    return 0;
  };

  const finalizarOrdem = async () => {
    if (!formaPagamento) {
      addToast('warning', 'Selecione uma forma de pagamento');
      return;
    }

    if (formaPagamento === 'DINHEIRO' && (!valorRecebido || parseFloat(valorRecebido) < parseFloat(ordemParaFinalizar.valorTotal))) {
      addToast('warning', 'Valor recebido insuficiente para pagamento em dinheiro');
      return;
    }

    try {
      const requestData = {
        formaPagamento: formaPagamento
      };

      const response = await axios.put(
        `${API_BASE_URL}/ordemservico/${ordemParaFinalizar.id}/finalizarOrdemServico`,
        requestData,
        getAuthHeader()
      );

      addToast('success', 'Ordem finalizada com sucesso!');
      fecharModalFinalizacao();
      carregarOrdens();

    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      addToast('error', 'Erro ao finalizar ordem: ' + errorMessage);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <Header />
        <div style={styles.loading}>Carregando ordens de serviço...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <Header />
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Header />

      <div style={styles.contentWrapper}>

        {/* Modal para seleção de tipo de impressão */}
        <Modal
          isOpen={modalImpressaoIsOpen}
          onRequestClose={fecharModalImpressao}
          style={modalImpressaoStyles}
          contentLabel="Selecionar Tipo de Impressão"
        >
          <div style={modalImpressaoStyles.header}>
            <h2 style={modalImpressaoStyles.title}>
              <FaPrint style={{ marginRight: '10px' }} />
              Selecionar Tipo de Impressão
            </h2>
            <button
              onClick={fecharModalImpressao}
              style={modalImpressaoStyles.closeButton}
            >
              <FaTimesCircle />
            </button>
          </div>

          {ordemSelecionada && (
            <div style={modalImpressaoStyles.content}>
              <p style={modalImpressaoStyles.info}>
                Ordem de Serviço: <strong>#{ordemSelecionada.numeroOrdem}</strong>
                <br />
                Cliente: <strong>{ordemSelecionada.clienteNome}</strong>
              </p>

              <div style={modalImpressaoStyles.options}>
                <div style={modalImpressaoStyles.option}>
                  <input
                    type="radio"
                    id="empresa_cliente"
                    name="tipo_impressao"
                    value="EMPRESA_CLIENTE"
                    checked={tipoImpressao === 'EMPRESA_CLIENTE'}
                    onChange={(e) => setTipoImpressao(e.target.value)}
                  />
                  <label htmlFor="empresa_cliente" style={modalImpressaoStyles.optionLabel}>
                    <div style={modalImpressaoStyles.optionTitle}>Completo (Empresa + Cliente)</div>
                    <div style={modalImpressaoStyles.optionDescription}>
                      Folha dupla com via detalhada para arquivo e via simplificada para cliente
                    </div>
                  </label>
                </div>

                <div style={modalImpressaoStyles.option}>
                  <input
                    type="radio"
                    id="apenas_empresa"
                    name="tipo_impressao"
                    value="APENAS_EMPRESA"
                    checked={tipoImpressao === 'APENAS_EMPRESA'}
                    onChange={(e) => setTipoImpressao(e.target.value)}
                  />
                  <label htmlFor="apenas_empresa" style={modalImpressaoStyles.optionLabel}>
                    <div style={modalImpressaoStyles.optionTitle}>Apenas Via da Empresa</div>
                    <div style={modalImpressaoStyles.optionDescription}>
                      Apenas a via detalhada para arquivo interno
                    </div>
                  </label>
                </div>

                <div style={modalImpressaoStyles.option}>
                  <input
                    type="radio"
                    id="apenas_cliente"
                    name="tipo_impressao"
                    value="APENAS_CLIENTE"
                    checked={tipoImpressao === 'APENAS_CLIENTE'}
                    onChange={(e) => setTipoImpressao(e.target.value)}
                  />
                  <label htmlFor="apenas_cliente" style={modalImpressaoStyles.optionLabel}>
                    <div style={modalImpressaoStyles.optionTitle}>Apenas Via do Cliente</div>
                    <div style={modalImpressaoStyles.optionDescription}>
                      Apenas a via simplificada para entrega ao cliente
                    </div>
                  </label>
                </div>
              </div>

              <div style={modalImpressaoStyles.buttons}>
                <button
                  onClick={fecharModalImpressao}
                  style={modalImpressaoStyles.cancelButton}
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmarImpressao}
                  style={modalImpressaoStyles.confirmButton}
                >
                  <FaPrint style={{ marginRight: '8px' }} />
                  Imprimir
                </button>
              </div>
            </div>
          )}
        </Modal>

        {/* Observação da OS (ícone no card): ler e editar */}
        {observacaoAberta && (
          <div style={styles.observacaoFundo} onClick={() => !salvandoObservacao && setObservacaoAberta(null)}>
            <div style={styles.observacaoCaixa} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <h3 style={{ margin: '0 0 10px 0', display: 'flex', alignItems: 'center', gap: 8, color: '#2c3e50' }}>
                <FaStickyNote style={{ color: '#d4a106' }} /> Observação da OS #{observacaoAberta.ordem.numeroOrdem}
              </h3>
              <textarea
                style={styles.observacaoTexto}
                value={observacaoAberta.texto}
                maxLength={MAX_OBSERVACAO}
                onChange={(e) => setObservacaoAberta({ ...observacaoAberta, texto: e.target.value })}
                autoFocus
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: '#7f8c8d' }}>{observacaoAberta.texto.length}/{MAX_OBSERVACAO} · em branco remove a observação</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" style={styles.cancelarButton} disabled={salvandoObservacao} onClick={() => setObservacaoAberta(null)}>
                    Cancelar
                  </button>
                  <button type="button" style={styles.concluirButton} disabled={salvandoObservacao} onClick={salvarObservacao}>
                    {salvandoObservacao ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Finalização */}
        <Modal
          isOpen={modalFinalizacaoIsOpen}
          onRequestClose={fecharModalFinalizacao}
          style={modalFinalizacaoStyles}
          contentLabel="Finalizar Ordem de Serviço"
        >
          <div style={modalFinalizacaoStyles.header}>
            <h2 style={modalFinalizacaoStyles.title}>
              <FaWallet style={{ marginRight: '10px' }} />
              Finalizar Ordem de Serviço
            </h2>
            <button
              onClick={fecharModalFinalizacao}
              style={modalFinalizacaoStyles.closeButton}
            >
              <FaTimesCircle />
            </button>
          </div>

          {ordemParaFinalizar && (
            <div style={modalFinalizacaoStyles.content}>
              {/* Informações do Cliente */}
              <div style={modalFinalizacaoStyles.clienteSection}>
                <h3 style={modalFinalizacaoStyles.sectionTitle}>Dados do Cliente</h3>
                <div style={modalFinalizacaoStyles.clienteInfo}>
                  <div style={modalFinalizacaoStyles.infoRow}>
                    <FaUser style={modalFinalizacaoStyles.infoIcon} />
                    <span><strong>Nome:</strong> {ordemParaFinalizar.clienteNome}</span>
                  </div>
                  <div style={modalFinalizacaoStyles.infoRow}>
                    <FaIdCard style={modalFinalizacaoStyles.infoIcon} />
                    <span><strong>Documento:</strong> {ordemParaFinalizar.clienteDocumento}</span>
                  </div>
                  <div style={modalFinalizacaoStyles.infoRow}>
                    <FaMoneyBillWave style={modalFinalizacaoStyles.infoIcon} />
                    <span><strong>Total a Pagar:</strong> R$ {parseFloat(ordemParaFinalizar.valorTotal).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>

              {/* Formas de Pagamento */}
              <div style={modalFinalizacaoStyles.pagamentoSection}>
                <h3 style={modalFinalizacaoStyles.sectionTitle}>Forma de Pagamento</h3>
                <div style={modalFinalizacaoStyles.formasPagamento}>
                  {formasPagamento.map((forma) => {
                    const Icone = forma.icone;
                    return (
                      <div
                        key={forma.id}
                        onClick={() => setFormaPagamento(forma.id)}
                        style={{
                          ...modalFinalizacaoStyles.formaPagamento,
                          borderColor: formaPagamento === forma.id ? forma.cor : '#ddd',
                          backgroundColor: formaPagamento === forma.id ? `${forma.cor}15` : 'white'
                        }}
                      >
                        <Icone
                          style={{
                            ...modalFinalizacaoStyles.formaIcone,
                            color: forma.cor
                          }}
                        />
                        <span style={modalFinalizacaoStyles.formaNome}>{forma.nome}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Campo para valor em dinheiro */}
              {formaPagamento === 'DINHEIRO' && (
                <div style={modalFinalizacaoStyles.dinheiroSection}>
                  <div style={modalFinalizacaoStyles.inputGroup}>
                    <label style={modalFinalizacaoStyles.label}>
                      Valor Recebido (R$):
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min={parseFloat(ordemParaFinalizar.valorTotal)}
                      value={valorRecebido}
                      onChange={(e) => setValorRecebido(e.target.value)}
                      style={modalFinalizacaoStyles.input}
                      placeholder="0,00"
                    />
                  </div>

                  {valorRecebido && parseFloat(valorRecebido) >= parseFloat(ordemParaFinalizar.valorTotal) && (
                    <div style={modalFinalizacaoStyles.trocoSection}>
                      <div style={modalFinalizacaoStyles.trocoValue}>
                        Troco: R$ {calcularTroco().toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                  )}

                  {valorRecebido && parseFloat(valorRecebido) < parseFloat(ordemParaFinalizar.valorTotal) && (
                    <div style={modalFinalizacaoStyles.aviso}>
                      Valor insuficiente. Faltam: R$ {(parseFloat(ordemParaFinalizar.valorTotal) - parseFloat(valorRecebido)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </div>
                  )}
                </div>
              )}

              {/* Botões de ação */}
              <div style={modalFinalizacaoStyles.buttons}>
                <button
                  onClick={fecharModalFinalizacao}
                  style={modalFinalizacaoStyles.cancelButton}
                >
                  Cancelar
                </button>
                <button
                  onClick={finalizarOrdem}
                  disabled={!formaPagamento || (formaPagamento === 'DINHEIRO' && (!valorRecebido || parseFloat(valorRecebido) < parseFloat(ordemParaFinalizar.valorTotal)))}
                  style={{
                    ...modalFinalizacaoStyles.confirmButton,
                    opacity: (!formaPagamento || (formaPagamento === 'DINHEIRO' && (!valorRecebido || parseFloat(valorRecebido) < parseFloat(ordemParaFinalizar.valorTotal)))) ? 0.6 : 1
                  }}
                >
                  <FaWallet style={{ marginRight: '8px' }} />
                  Finalizar Ordem
                </button>
              </div>
            </div>
          )}
        </Modal>

        {/* Cabeçalho */}
        <div style={styles.header}>
          <div style={styles.titleContainer}>
            <FaFileAlt style={styles.titleIcon} />
            <h1 style={styles.title}>ORDENS DE SERVIÇO</h1>
          </div>

          <div style={styles.buttonsContainer}>
            <button onClick={novaOrdem} style={styles.novaOrdemButton}>
              <FaPlus style={{ marginRight: 8 }} />
              Nova Ordem
            </button>

            <button
              onClick={(e) => carregarOrdens(e.target.value)}
              style={styles.recarregarButton}
            >
              <FaSync style={{ marginRight: 8 }} />
              Recarregar
            </button>
          </div>
        </div>

        {/* Filtros na tela principal */}
        <div style={styles.filtrosContainer}>
          <div style={styles.filtrosHeader}>
            <FaFilter style={styles.filtrosIcon} />
            <h3 style={styles.filtrosTitle}>Filtros</h3>
            <button onClick={limparFiltros} style={styles.limparFiltrosButton}>
              Limpar Filtros
            </button>
          </div>

          <div style={styles.filtrosContent}>
            {/* Filtro por período */}
            <div style={styles.filtroGrupo}>
              <label style={styles.filtroLabel}>Período:</label>
              <div style={styles.filtroInputs}>
                <input
                  type="date"
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                  style={styles.filtroInput}
                  max={formatarDataParaAPI(new Date())}
                />
                <span style={styles.ateLabel}>até</span>
                <input
                  type="date"
                  value={dataFim}
                  onChange={(e) => setDataFim(e.target.value)}
                  style={styles.filtroInput}
                  max={formatarDataParaAPI(new Date())}
                />
                <button
                  onClick={aplicarFiltroPeriodo}
                  style={styles.aplicarFiltroButton}
                  disabled={!dataInicio || !dataFim}
                >
                  Aplicar
                </button>
              </div>
            </div>

            {/* Filtro por CPF/CNPJ */}
            <div style={styles.filtroGrupo}>
              <label style={styles.filtroLabel}>CPF/CNPJ:</label>
              <div style={styles.filtroInputs}>
                <input
                  type="text"
                  value={filtroCpfCnpj}
                  onChange={(e) => setFiltroCpfCnpj(e.target.value)}
                  placeholder="Digite o CPF ou CNPJ"
                  style={styles.filtroInputText}
                />
                <button
                  onClick={aplicarFiltroCpfCnpj}
                  style={styles.aplicarFiltroButton}
                  disabled={!filtroCpfCnpj}
                >
                  Aplicar
                </button>
              </div>
            </div>

            {/* Filtro por status */}
            <div style={styles.filtroGrupo}>
              <label style={styles.filtroLabel}>Status:</label>
              <select
                value={filtroStatus}
                onChange={(e) => setFiltroStatus(e.target.value)}
                style={styles.filtroSelect}
              >
                <option value="TODOS">Todos os status</option>
                <option value="ABERTA">Aberta</option>
                <option value="CONCLUIDA">Concluída</option>
                <option value="CANCELADA">Cancelada</option>
              </select>
            </div>

            {/* Filtro rápido - Hoje */}
            <div style={styles.filtroGrupo}>
              <span>&nbsp;</span>
              <button
                onClick={() => {
                  setModoFiltro('hoje');
                  carregarOrdens();
                }}
                style={{
                  ...styles.filtroRapidoButton,
                  ...(modoFiltro === 'hoje' ? styles.filtroRapidoButtonAtivo : {})
                }}
              >
                <FaCalendarDay style={{ marginRight: '5px' }} />
                Ordens de Hoje
              </button>
            </div>
          </div>

          {/* Informações do filtro aplicado */}
          <div style={styles.filtroInfo}>
            {modoFiltro === 'hoje' && (
              <span style={styles.filtroTag}>
                <FaCalendarDay /> Mostrando ordens de hoje
              </span>
            )}
            {modoFiltro === 'periodo' && dataInicio && dataFim && (
              <span style={styles.filtroTag}>
                <FaCalendarWeek /> Período: {formatarData(dataInicio)} à {formatarData(dataFim)}
              </span>
            )}
            {modoFiltro === 'cpfCnpj' && filtroCpfCnpj && (
              <span style={styles.filtroTag}>
                <FaIdCard /> CPF/CNPJ: {filtroCpfCnpj}
              </span>
            )}
            {filtroStatus !== 'TODOS' && (
              <span style={{
                ...styles.filtroTag,
                backgroundColor: getStatusColor(filtroStatus).bg,
                color: getStatusColor(filtroStatus).text
              }}>
                Status: {filtroStatus.replace('_', ' ')}
              </span>
            )}
          </div>
        </div>

        {/* Lista de ordens */}
        <div style={styles.ordensContainer}>
          {ordensFiltradas.length === 0 ? (
            <div style={styles.noResults}>
              {modoFiltro === 'cpfCnpj' || (modoFiltro === 'periodo' && dataInicio && dataFim) || filtroStatus !== 'TODOS' ?
                'Nenhuma ordem encontrada com os filtros aplicados' :
                'Nenhuma ordem de serviço cadastrada'}
            </div>
          ) : (
            ordensFiltradas.map((ordem) => (
              <div key={ordem.id} style={styles.ordemCard}>
                {/* Lado Esquerdo - Informações principais */}
                <div style={styles.ordemContent}>
                  {/* Header */}
                  <div style={styles.ordemHeader}>
                    <div style={styles.ordemInfo}>
                      <div style={styles.ordemNumero}>
                        OS #{ordem.numeroOrdem}
                        {/* Tem observação: o ícone abre a caixa para ler e editar */}
                        {ordem.observacoes && (
                          <button type="button" style={styles.observacaoIcone} title={ordem.observacoes}
                            aria-label="Ver e editar a observação"
                            onClick={() => setObservacaoAberta({ ordem, texto: ordem.observacoes })}>
                            <FaStickyNote />
                          </button>
                        )}
                      </div>
                      <div style={styles.ordemData}>
                        <FaCalendarAlt style={{ marginRight: 5 }} />
                        {formatarData(ordem.dataCadastro)}
                      </div>
                    </div>

                    <div style={{
                      ...styles.statusBadge,
                      backgroundColor: getStatusColor(ordem.statusOrdemServico).bg,
                      color: getStatusColor(ordem.statusOrdemServico).text
                    }}>
                      {ordem.statusOrdemServico.replace('_', ' ')}
                    </div>
                  </div>

                  {/* Informações do cliente e veículo */}
                  <div style={styles.ordemBody}>
                    <div style={styles.clienteInfo}>
                      <FaUser style={styles.clienteIcon} />
                      <span style={styles.clienteNome}>{ordem.clienteNome}</span>
                      <span style={styles.clienteDocumento}>
                        <FaIdCard style={{ marginRight: 5 }} />
                        {ordem.clienteDocumento}
                      </span>
                    </div>

                    <div style={styles.veiculoInfo}>
                      <FaCar style={styles.veiculoIcon} />
                      <span>{ordem.quantidadeVeiculos} veículo(s)</span>
                    </div>

                    <div style={styles.valorTotal}>
                      <FaMoneyBillWave style={styles.valorIcon} />
                      <span>Total: R$ {parseFloat(ordem.valorTotal).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                      {/* Forma de pagamento escolhida ao finalizar (mesmo ícone e cor do modal de finalização) */}
                      {(() => {
                        const forma = formasPagamento.find((f) => f.id === ordem.formaPagamento);
                        if (!forma) return null;
                        const Icone = forma.icone;
                        return (
                          <span style={{ ...styles.pagamentoBadge, color: forma.cor, borderColor: forma.cor, backgroundColor: `${forma.cor}15` }}
                            title="Forma de pagamento informada ao finalizar">
                            <Icone style={{ marginRight: 4 }} />{forma.nome}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Lado Direito - Todos os botões */}
                <div style={styles.botoesContainer}>
                  {/* Botões de Status */}
                  {ordem.statusOrdemServico === 'ABERTA' && (
                    <div style={styles.statusButtons}>
                      <button
                        onClick={() => abrirModalFinalizacao(ordem)}
                        style={styles.concluirButton}
                      >
                        <FaWallet style={{ marginRight: 5 }} />
                        Finalizar
                      </button>
                      <button
                        onClick={() => alterarStatus(ordem.id, 'CANCELADA')}
                        style={styles.cancelarButton}
                      >
                        <FaTimesCircle style={{ marginRight: 5 }} />
                        Cancelar
                      </button>
                    </div>
                  )}

                  {/* Status info quando não for ABERTA */}
                  {ordem.statusOrdemServico !== 'ABERTA' && (
                    <div style={styles.statusInfo}>
                      Status: <strong>{ordem.statusOrdemServico.replace('_', ' ')}</strong>
                    </div>
                  )}

                  {/* Botões de Ação */}
                  {ordem.statusOrdemServico !== 'CANCELADA' && (
                    <div style={styles.acaoButtons}>
                      {ordem.statusOrdemServico !== 'CONCLUIDA' && (
                        <button
                          onClick={() => editarOrdem(ordem.id)}
                          style={styles.editButton}
                        >
                          <FaEdit /> Editar
                        </button>
                      )}
                      <button
                        onClick={() => abrirModalImpressao(ordem)}
                        style={styles.printButton}
                      >
                        <FaPrint /> Imprimir
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={closeConfirm}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText={valorConfirm}
          cancelText="Sair"
        />
        {/* Componente de Paginação */}
        {ordensFiltradas.length > 0 && (
          <div style={styles.paginacaoContainer}>
            <div style={styles.paginacaoInfo}>
              Mostrando {ordensFiltradas.length} de {totalElementos} ordens
            </div>

            <div style={styles.paginacaoControles}>
              <select
                value={tamanhoPagina}
                onChange={(e) => alterarTamanhoPagina(Number(e.target.value))}
                style={styles.paginacaoSelect}
              >
                <option value={5}>5 por página</option>
                <option value={10}>10 por página</option>
                <option value={20}>20 por página</option>
                <option value={50}>50 por página</option>
              </select>

              <button
                onClick={irParaPrimeiraPagina}
                disabled={paginaAtual === 0}
                style={styles.paginacaoBotao}
              >
                <FaStepBackward />
              </button>

              <button
                onClick={retrocederPagina}
                disabled={paginaAtual === 0}
                style={styles.paginacaoBotao}
              >
                <FaChevronLeft />
              </button>

              <span style={styles.paginacaoInfoPagina}>
                Página {paginaAtual + 1} de {totalPaginas}
              </span>

              <button
                onClick={avancarPagina}
                disabled={paginaAtual >= totalPaginas - 1}
                style={styles.paginacaoBotao}
              >
                <FaChevronRight />
              </button>

              <button
                onClick={irParaUltimaPagina}
                disabled={paginaAtual >= totalPaginas - 1}
                style={styles.paginacaoBotao}
              >
                <FaStepForward />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Estilos
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
  },
  contentWrapper: {
    paddingLeft: '35px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd'
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  },
  titleIcon: {
    fontSize: '28px',
    color: '#333'
  },
  title: {
    fontSize: '24px',
    margin: 0,
    color: '#333',
    fontWeight: '600'
  },
  buttonsContainer: {
    display: 'flex',
    gap: '15px'
  },
  novaOrdemButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 15px',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
    '&:hover': {
      backgroundColor: '#45a049'
    }
  },
  recarregarButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 15px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
    '&:hover': {
      backgroundColor: '#0b7dda'
    }
  },
  filtrosContainer: {
    backgroundColor: '#fff',
    margin: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    overflow: 'hidden'
  },
  filtrosHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '15px 20px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #eee'
  },
  filtrosIcon: {
    color: '#6c757d',
    marginRight: '10px'
  },
  filtrosTitle: {
    margin: 0,
    fontSize: '18px',
    color: '#333',
    flex: 1
  },
  limparFiltrosButton: {
    padding: '5px 10px',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    '&:hover': {
      backgroundColor: '#c82333'
    }
  },
  filtrosContent: {
    padding: '20px',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '20px'
  },
  filtroGrupo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  filtroLabel: {
    fontWeight: '600',
    color: '#333',
    fontSize: '14px'
  },
  filtroInputs: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  filtroInput: {
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    fontSize: '14px'
  },
  filtroInputText: {
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    fontSize: '14px',
    flex: 1
  },
  ateLabel: {
    color: '#6c757d',
    fontSize: '14px'
  },
  aplicarFiltroButton: {
    padding: '8px 12px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    '&:hover': {
      backgroundColor: '#218838'
    },
    '&:disabled': {
      backgroundColor: '#6c757d',
      cursor: 'not-allowed'
    }
  },
  filtroSelect: {
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    fontSize: '14px',
    backgroundColor: 'white'
  },
  filtroRapidoButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    backgroundColor: '#f8f9fa',
    color: '#212529',
    border: '1px solid #ddd',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    '&:hover': {
      backgroundColor: '#e2e6ea'
    }
  },
  filtroRapidoButtonAtivo: {
    backgroundColor: '#007bff',
    color: 'white',
    borderColor: '#007bff'
  },
  filtroInfo: {
    display: 'flex',
    gap: '10px',
    padding: '15px 20px',
    backgroundColor: '#f8f9fa',
    borderTop: '1px solid #eee',
    flexWrap: 'wrap'
  },
  filtroTag: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    padding: '5px 10px',
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    borderRadius: '15px',
    fontSize: '14px',
    fontWeight: '500'
  },
  ordensContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
    gap: '20px',
    padding: '0 20px 20px 20px',
    flex: 1,
    '@media (max-width: 1100px)': {
      gridTemplateColumns: '1fr'
    }
  },
  ordemCard: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    transition: 'transform 0.2s, box-shadow 0.2s',
    display: 'flex',
    flexDirection: 'row',
    minHeight: '160px',
    width: '100%',
    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
    }
  },
  ordemContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: '15px',
    gap: '10px'
  },
  ordemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '10px'
  },
  ordemInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
  },
  ordemNumero: {
    fontWeight: '600',
    color: '#2c3e50',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center'
  },
  observacaoIcone: {
    marginLeft: '8px',
    padding: '2px 5px',
    border: '1px solid #f1c40f',
    borderRadius: '6px',
    background: '#fffbea',
    color: '#d4a106',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '13px'
  },
  observacaoFundo: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1100, padding: '16px'
  },
  observacaoCaixa: {
    background: 'white', borderRadius: '10px', padding: '18px', width: '100%', maxWidth: '480px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.25)'
  },
  observacaoTexto: {
    width: '100%', minHeight: '120px', padding: '10px', border: '1px solid #ddd', borderRadius: '6px',
    fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box'
  },
  ordemData: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '13px',
    color: '#7f8c8d'
  },
  statusBadge: {
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600',
    flexShrink: 0
  },
  pagamentoBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: '10px',
    padding: '2px 8px',
    border: '1px solid',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600'
  },
  ordemBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1,
    justifyContent: 'center'
  },
  clienteInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px'
  },
  clienteIcon: {
    color: '#3498db',
    marginRight: '5px',
    fontSize: '14px'
  },
  clienteNome: {
    fontWeight: '600',
    fontSize: '15px',
    color: '#2c3e50'
  },
  clienteDocumento: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '13px',
    color: '#7f8c8d'
  },
  veiculoInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#555'
  },
  veiculoIcon: {
    color: '#e74c3c',
    fontSize: '14px'
  },
  valorTotal: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: '600',
    color: '#2ecc71',
    fontSize: '15px'
  },
  valorIcon: {
    fontSize: '14px'
  },
  botoesContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '15px',
    borderLeft: '1px solid #eee',
    minWidth: '150px',
    flexShrink: 0,
    gap: '10px'
  },
  statusButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  concluirButton: {
    padding: '8px 12px',
    backgroundColor: '#27ae60',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '600',
    transition: 'all 0.2s',
    width: '100%',
    '&:hover': {
      backgroundColor: '#219a52'
    }
  },
  cancelarButton: {
    padding: '8px 12px',
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '600',
    transition: 'all 0.2s',
    width: '100%',
    '&:hover': {
      backgroundColor: '#c0392b'
    }
  },
  statusInfo: {
    padding: '8px',
    backgroundColor: '#f8f9fa',
    borderRadius: '4px',
    textAlign: 'center',
    fontSize: '12px',
    color: '#666',
    marginBottom: '10px'
  },
  acaoButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  editButton: {
    padding: '8px 12px',
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '5px',
    fontSize: '12px',
    width: '100%',
    '&:hover': {
      backgroundColor: '#2980b9'
    }
  },
  printButton: {
    padding: '8px 12px',
    backgroundColor: '#95a5a6',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '5px',
    fontSize: '12px',
    width: '100%',
    '&:hover': {
      backgroundColor: '#7f8c8d'
    }
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    fontSize: '18px',
    color: '#7f8c8d'
  },
  error: {
    padding: '40px',
    textAlign: 'center',
    fontSize: '18px',
    color: '#e74c3c'
  },
  noResults: {
    gridColumn: '1 / -1',
    padding: '40px',
    textAlign: 'center',
    fontSize: '16px',
    color: '#7f8c8d',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  paginacaoContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px 20px',
    backgroundColor: '#fff',
    borderTop: '1px solid #eee',
    margin: '0 20px 20px 20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  paginacaoInfo: {
    fontSize: '14px',
    color: '#666',
    fontWeight: '500'
  },
  paginacaoControles: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  paginacaoSelect: {
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    backgroundColor: 'white',
    fontSize: '14px'
  },
  paginacaoBotao: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 12px',
    border: '1px solid #ddd',
    backgroundColor: 'white',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#333',
    '&:hover:not(:disabled)': {
      backgroundColor: '#f8f9fa'
    },
    '&:disabled': {
      backgroundColor: '#f8f9fa',
      color: '#6c757d',
      cursor: 'not-allowed'
    }
  },
  paginacaoInfoPagina: {
    fontSize: '14px',
    color: '#333',
    fontWeight: '500',
    margin: '0 10px'
  }
};

// Estilos para a modal de impressão
const modalImpressaoStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '500px',
    width: '90%',
    borderRadius: '8px',
    padding: '0',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    border: 'none'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #eee',
    backgroundColor: '#f8f9fa'
  },
  title: {
    margin: 0,
    color: '#333',
    fontSize: '18px',
    display: 'flex',
    alignItems: 'center'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    color: '#999',
    cursor: 'pointer',
    padding: '5px',
    '&:hover': {
      color: '#666'
    }
  },
  content: {
    padding: '20px'
  },
  info: {
    margin: '0 0 20px 0',
    color: '#666',
    lineHeight: '1.5'
  },
  options: {
    marginBottom: '20px'
  },
  option: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '15px',
    padding: '10px',
    borderRadius: '6px',
    border: '1px solid #ddd',
    transition: 'all 0.2s',
    '&:hover': {
      borderColor: '#2196F3',
      backgroundColor: '#f8f9fa'
    }
  },
  optionLabel: {
    marginLeft: '10px',
    cursor: 'pointer',
    flex: 1
  },
  optionTitle: {
    fontWeight: '600',
    color: '#333',
    marginBottom: '5px'
  },
  optionDescription: {
    fontSize: '14px',
    color: '#666',
    lineHeight: '1.4'
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '20px'
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#f5f5f5',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 'bold',
    '&:hover': {
      backgroundColor: '#e0e0e0'
    }
  },
  confirmButton: {
    padding: '10px 20px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: '#1976d2'
    }
  }
};

// Estilos para a modal de finalização
const modalFinalizacaoStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 1001
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '500px',
    width: '90%',
    maxHeight: '90vh',
    borderRadius: '12px',
    padding: '0',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
    border: 'none',
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 25px',
    borderBottom: '1px solid #eee',
    backgroundColor: '#f8f9fa'
  },
  title: {
    margin: 0,
    color: '#2c3e50',
    fontSize: '20px',
    display: 'flex',
    alignItems: 'center',
    fontWeight: '600'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '22px',
    color: '#7f8c8d',
    cursor: 'pointer',
    padding: '5px',
    borderRadius: '50%',
    transition: 'all 0.2s',
    '&:hover': {
      color: '#e74c3c',
      backgroundColor: 'rgba(231, 76, 60, 0.1)'
    }
  },
  content: {
    padding: '25px'
  },
  clienteSection: {
    marginBottom: '25px'
  },
  sectionTitle: {
    margin: '0 0 15px 0',
    color: '#2c3e50',
    fontSize: '16px',
    fontWeight: '600',
    borderBottom: '2px solid #3498db',
    paddingBottom: '8px'
  },
  clienteInfo: {
    backgroundColor: '#f8f9fa',
    padding: '15px',
    borderRadius: '8px',
    border: '1px solid #e9ecef'
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px',
    fontSize: '14px',
    color: '#2c3e50',
    '&:last-child': {
      marginBottom: 0
    }
  },
  infoIcon: {
    marginRight: '10px',
    color: '#3498db',
    width: '16px'
  },
  pagamentoSection: {
    marginBottom: '20px'
  },
  formasPagamento: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '10px'
  },
  formaPagamento: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '15px 10px',
    border: '2px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    backgroundColor: 'white',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
    }
  },
  formaIcone: {
    fontSize: '24px',
    marginBottom: '8px'
  },
  formaNome: {
    fontSize: '14px',
    fontWeight: '600',
    textAlign: 'center'
  },
  dinheiroSection: {
    marginBottom: '20px',
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e9ecef'
  },
  inputGroup: {
    marginBottom: '15px'
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    color: '#2c3e50',
    fontSize: '14px'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '2px solid #ddd',
    borderRadius: '6px',
    fontSize: '16px',
    transition: 'border-color 0.2s',
    '&:focus': {
      outline: 'none',
      borderColor: '#3498db'
    }
  },
  trocoSection: {
    padding: '12px',
    backgroundColor: '#d4edda',
    border: '1px solid #c3e6cb',
    borderRadius: '6px',
    textAlign: 'center'
  },
  trocoValue: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#155724'
  },
  aviso: {
    padding: '12px',
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '6px',
    textAlign: 'center',
    color: '#721c24',
    fontSize: '14px',
    fontWeight: '600'
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '20px'
  },
  cancelButton: {
    padding: '12px 24px',
    backgroundColor: '#f8f9fa',
    color: '#6c757d',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '14px',
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: '#e2e6ea'
    }
  },
  confirmButton: {
    padding: '12px 24px',
    backgroundColor: '#27ae60',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.2s',
    '&:hover:not(:disabled)': {
      backgroundColor: '#219a52'
    },
    '&:disabled': {
      cursor: 'not-allowed'
    }
  }
};

export default OrdemServicoView;